import { useEffect, useState } from "react";

import { useAuth0 } from "@auth0/auth0-react";
import axios from "axios";

import { attemptStaleReload } from "@features/appVersion/deployVersionGuard";
import { isAppStale } from "@features/appVersion/staleAppState";
import client from "@services/api";
import parseApiErrorMessage from "@services/api/parseApiErrorMessage";

import handleSessionExpired from "./handleSessionExpired";
import { LS_AUTH_TOKEN } from "./localstorage";

export type AuthTokenState =
  | { status: "auth0Loading" }
  | { status: "loading" }
  | { status: "signedOut" }
  | { status: "ready" }
  | { status: "error"; error: unknown; message: string };

type AuthPayload = {
  access_token: string;
  expires_at: number;
};

function getTokenFromLocalStorage(): AuthPayload | null {
  const tokenString = window.localStorage.getItem(LS_AUTH_TOKEN);
  if (!tokenString) return null;
  const token = JSON.parse(tokenString) as AuthPayload;
  if (token.expires_at * 1000 <= Date.now()) return null;
  return token;
}

function saveTokenToLocalStorage(accessToken: string, expiresAt: number): void {
  const payload: AuthPayload = {
    access_token: accessToken,
    expires_at: expiresAt,
  };
  window.localStorage.setItem(LS_AUTH_TOKEN, JSON.stringify(payload));
}

// Helper function to set auth token in headers
function setAuthHeaders(token: string) {
  const authToken = `Bearer ${token}`;
  axios.defaults.headers.common["Authorization"] = authToken;
  client.headers["Authorization"] = authToken;
}

/** Reload before marking auth as ready when this tab is stale relative to a newer deploy. */
function reloadIfStaleBeforeAuthReady(): boolean {
  return isAppStale() && attemptStaleReload();
}

const useAuthToken = () => {
  const [authState, setAuthState] = useState<AuthTokenState>({
    status: "loading",
  });
  const {
    isAuthenticated,
    getAccessTokenSilently,
    getIdTokenClaims,
    isLoading,
  } = useAuth0();

  useEffect(() => {
    let active = true;
    let timeout: NodeJS.Timeout | undefined;
    const setExpTimeout = (expiresAt: number) => {
      const timeToExpire = expiresAt * 1000 - Date.now();
      return setTimeout(handleSessionExpired, timeToExpire);
    };

    async function setTokens() {
      try {
        const jwt = await getAccessTokenSilently();
        const claims = await getIdTokenClaims();

        const { data } = await axios.post(`/auth/token`, {
          auth0_token: jwt,
        });

        if (!active) return;
        setAuthHeaders(data.access_token);

        if (claims?.exp) {
          saveTokenToLocalStorage(data.access_token, claims.exp);
          timeout = setExpTimeout(claims.exp);
        }

        if (reloadIfStaleBeforeAuthReady()) return;
        setAuthState({ status: "ready" });
      } catch (error: unknown) {
        const message =
          error instanceof Error
            ? await parseApiErrorMessage(error)
            : String(error);
        if (!active) return;
        setAuthState({ status: "error", error, message });
        console.error(error);
      }
    }
    // First check if we have a valid token in localStorage
    const storedToken = getTokenFromLocalStorage();
    if (storedToken) {
      setAuthHeaders(storedToken.access_token);
      const didReload = reloadIfStaleBeforeAuthReady();
      if (!didReload) {
        setAuthState({ status: "ready" });
        timeout = setExpTimeout(storedToken.expires_at);
      }
    } else {
      if (isLoading) {
        setAuthState({
          status: isAuthenticated ? "loading" : "auth0Loading",
        });
      } else if (isAuthenticated) {
        setAuthState({ status: "loading" });
        setTokens();
      } else {
        // Clear auth headers when not authenticated
        delete axios.defaults.headers.common["Authorization"];
        delete client.headers["Authorization"];
        setAuthState({ status: "signedOut" });
      }
    }
    return () => {
      active = false;
      if (timeout) clearTimeout(timeout);
    };
  }, [isLoading, isAuthenticated, getAccessTokenSilently, getIdTokenClaims]);

  return authState;
};

export default useAuthToken;
